import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Table, TableBody,
  Tab, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import AnimationPage from '../../pages/AnimationPage';
import ConfirmModal from '../common/modal/ConfirmModal';
import CustomCheckbox from '../CustomCheckbox';
import { CommonExcelButton } from '../../form-components/CommonExcelButton';
import { FloatingActionBar, FloatingBarInfo, FloatingCloseButton } from '../member/memberList/MemberListStyled';
import {
  changeReservationAttendance, exportUnconfirmedReservations, getUnconfirmedReservations,
  processUnconfirmedReservations,
} from '../../api/autoAttendance';
import ChangeAttendanceModal from './ChangeAttendanceModal';
import ScheduleDateLink from './ScheduleDateLink';
import ClassAttendanceStats from './ClassAttendanceStats';
import ClassManageSettings from './ClassManageSettings';
import ConsultList from './ConsultList';
import ConsultStats from './ConsultStats';
import ReservationHistory from './ReservationHistory';
import {
  bodyCellSx, CLASS_TYPE_OPTIONS, dateFieldSx, headCellSx, SEARCH_FIELDS, searchButtonSx,
} from './classManageShared';
import {
  HeaderSummaryChips, SummaryChipDivider, SummaryChipItem,
} from '../lesson/privateLesson/PrivateLessonStyled';
import { getPointTrainers } from '../../api/staff';
import { errorMessages, exportStyledExcel } from '../../utils/methods';

const PAGE_SIZE = 50;
// 통계·설정 하위 탭(알약 모양, 센터 설정 레슨 탭과 같은 모양)
const pillTabsSx = {
  minHeight: 36,
  '& .MuiTab-root': {
    borderRadius: '8px', border: '1px solid #ddd', mr: 1.25, minWidth: '80px', minHeight: '36px !important',
    padding: '8px 20px', fontWeight: 500, fontSize: '13px', color: '#666', textTransform: 'none',
  },
  '& .MuiTab-root.Mui-selected': { background: '#262626', border: '1px solid #262626', color: '#fff' },
  '& .MuiTabs-indicator': { display: 'none' },
};
// 이력 탭은 운영에서 예약 처리 이력(reservation_action_log)이 제대로 쌓이는지 본 뒤에 연다(10/15 사용자 결정).
// 기록은 백엔드에서 계속된다.
const SHOW_HISTORY_TAB = true;
const DATE_FORMAT = 'YYYY-MM-DD';

// 출석·조회 버튼: 파란 테두리 + 투명 배경
const BLUE = '#1976D2';
const BLUE_HOVER = 'rgba(25, 118, 210, 0.06)';

const ACTIONS = {
  ATTEND: { label: '출석', confirmText: '출석', color: BLUE },
  ABSENT: { label: '결석', confirmText: '결석', color: '#F44336' },
  CANCEL: { label: '예약취소', confirmText: '예약취소', color: '#F44336' },
};

const STATUS_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'UNCONFIRMED', label: '미확인' },
  { value: 'ATTENDED', label: '출석' },
  { value: 'ABSENT', label: '결석' },
];

// 출결 상태 표시
const STATUS_BADGE = {
  UNCONFIRMED: { label: '미확인', color: '#E65100', background: '#FFF3E0' },
  RESERVED: { label: '예약중', color: '#1565C0', background: '#E3F2FD' },
  ATTENDED: { label: '출석', color: '#0F7F85', background: '#E0F2F1' },
  ABSENT: { label: '결석', color: '#F44336', background: '#FFEBEE' },
};
// 체크해서 일괄 처리할 수 있는 줄: 예약중(수업 전·진행 중 포함)·미확인과 이미 출석·결석된 줄
const isSelectable = row => ['RESERVED', 'UNCONFIRMED', 'ATTENDED', 'ABSENT'].includes(row.status);
const isSettled = row => row?.status === 'ATTENDED' || row?.status === 'ABSENT';
// 예약중·미확인·출석·결석 줄은 [변경]으로 출석·결석·예약취소를 고른다
const isChangeable = row => ['RESERVED', 'UNCONFIRMED', 'ATTENDED', 'ABSENT'].includes(row.status);

// 상태별 건수 칩 색(지점 스케줄 ATTEND_STATUS_META 와 같은 계열). 예약중은 수업 전이라 필터 없이 건수만
const SUMMARY_CHIPS = [
  { key: 'UNCONFIRMED', label: '미확인', color: '#E65100', filterable: true },
  { key: 'RESERVED', label: '예약중', color: '#5B6BC0', filterable: false },
  { key: 'ATTENDED', label: '출석', color: '#1E7E78', filterable: true },
  { key: 'ABSENT', label: '결석', color: '#C7362A', filterable: true },
];

const CANCEL_NOTICE = '개인·듀엣레슨은 예약을 취소하면 수업도 함께 삭제됩니다.';

const defaultFilter = () => ({
  startDate: dayjs().subtract(30, 'day').format(DATE_FORMAT),
  endDate: dayjs().format(DATE_FORMAT),
  classType: '',
  trainerId: '',
  status: 'ALL',
  member: '',
  className: '',
});

// 액션바 버튼: 출석은 채움, 결석·예약취소는 테두리. 높이·폭을 맞춘다
const barButtonSx = action => {
  const color = ACTIONS[action].color;
  const base = {
    minWidth: '84px', height: '34px', px: '14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
    boxShadow: 'none', whiteSpace: 'nowrap',
  };
  const hover = action === 'ATTEND' ? BLUE_HOVER : 'rgba(244, 67, 54, 0.06)';
  return { ...base, background: 'transparent', borderColor: color, color, '&:hover': { borderColor: color, background: hover } };
};

// 조회 기간 상한(서버와 같음). 운영 실측으로 정한 값
const MAX_PERIOD_MONTHS = 6;

// 체크박스 열은 폭을 고정하고 머리·본문 같은 크기로 가운데 둔다(기본 padding="checkbox" 는 왼쪽 테두리에 붙는다)
const checkboxCellSx = { width: 48, px: 0 };

function UnconfirmedAttendance() {
  const user = useSelector(state => state.user.user);
  const branchId = user?.branch?.branchId;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(defaultFilter);
  const [query, setQuery] = useState(defaultFilter);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // 확인 모달 대상 { action, ids }
  const [pending, setPending] = useState(null);
  const [result, setResult] = useState(null);
  // 실패 목록에 회원명·수업일을 보여주려고 조회했던 행을 기억한다
  const rowCache = useRef(new Map());

  const { data: trainers = [] } = useQuery({
    queryKey: ['unconfirmedTrainers', branchId],
    queryFn: () => getPointTrainers(branchId, user.token),
    enabled: !!branchId && !!user?.token,
  });

  // 무한 스크롤: 표 안에서 끝에 닿으면 다음 50건을 붙인다
  const { data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['unconfirmedReservations', branchId, query],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const res = await getUnconfirmedReservations(
          { branchId, ...query, page: pageParam, size: PAGE_SIZE },
          user.token,
        );
        (res?.content || []).forEach(row => rowCache.current.set(row.reservationId, row));
        return res;
      },
      getNextPageParam: lastPage =>
        lastPage && lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
      enabled: !!branchId && !!user?.token,
    });

  const rows = useMemo(() => (data?.pages || []).flatMap(p => p?.content || []), [data]);
  const totalElements = data?.pages?.[0]?.totalElements ?? 0;
  // 상태 필터를 뺀 같은 조건의 상태별 건수(첫 페이지 응답에만 온다)
  const statusCounts = data?.pages?.[0]?.statusCounts || {};

  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target || !hasNextPage) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length]);

  const selectableRows = rows.filter(isSelectable);
  const allChecked = selectableRows.length > 0 && selectableRows.every(row => selectedIds.has(row.reservationId));

  const processMutation = useMutation({
    mutationFn: ({ action, ids }) => processUnconfirmedReservations(branchId, { action, reservationIds: ids }, user.token),
    onSuccess: (res, { action }) => {
      setResult({ action, successCount: res?.successCount ?? 0, failures: res?.failures || [] });
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['unconfirmedReservations'] });
      queryClient.invalidateQueries({ queryKey: ['classAttendanceStats'] });
    },
    onError: err => toast.error(errorMessages(err)),
    onSettled: () => setPending(null),
  });

  const applyStatus = status => {
    const next = { ...query, status };
    setForm(next);
    setQuery(next);
    setSelectedIds(new Set());
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const [searchField, setSearchField] = useState('member');
  const [searchText, setSearchText] = useState('');

  // 검색어를 칩 조건으로 붙이고 바로 조회한다(같은 종류는 새 값으로 바뀐다)
  const addSearchChip = () => {
    const text = searchText.trim();
    if (!text) return;
    const next = { ...form, [searchField]: text };
    setSearchText('');
    runSearch(next);
  };

  const removeSearchChip = field => runSearch({ ...form, [field]: '' });

  const handleSearch = () => runSearch(form);

  const runSearch = next => {
    if (next.startDate > next.endDate) {
      toast.error('시작일이 종료일보다 늦을 수 없습니다.');
      return;
    }
    if (dayjs(next.endDate).isAfter(dayjs(next.startDate).add(MAX_PERIOD_MONTHS, 'month'))) {
      toast.error(`조회 기간은 최대 ${MAX_PERIOD_MONTHS}개월입니다.`);
      return;
    }
    // 같은 조건으로 다시 누르면 queryKey 가 같아 새로 불러오지 않으므로 직접 다시 부른다
    if (JSON.stringify(next) === JSON.stringify(query)) {
      refetch();
    }
    setForm(next);
    setQuery(next);
    setSelectedIds(new Set());
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  // CustomCheckbox(회원 목록과 같은 체크 이미지)는 클릭 1회에 핸들러가 2번 불리므로 토글이 아니라 체크 값으로 넣고 뺀다
  const setRowChecked = (id, checked) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const setAllChecked = checked =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      selectableRows.forEach(row => (checked ? next.add(row.reservationId) : next.delete(row.reservationId)));
      return next;
    });

  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [changeRow, setChangeRow] = useState(null);
  const [tab, setTab] = useState('list');
  const [statsTab, setStatsTab] = useState('class');

  const changeMutation = useMutation({
    mutationFn: action => changeReservationAttendance(
      branchId, { reservationId: changeRow.reservationId, action }, user.token),
    onSuccess: () => {
      toast.success('출결이 변경되었습니다');
      // 변경한 줄이 선택돼 있었으면 빼 둔다(예약취소된 줄이 일괄 처리에 남지 않게)
      if (changeRow) {
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(changeRow.reservationId);
          return next;
        });
      }
      setChangeRow(null);
      queryClient.invalidateQueries({ queryKey: ['unconfirmedReservations'] });
      queryClient.invalidateQueries({ queryKey: ['classAttendanceStats'] });
    },
    onError: err => toast.error(errorMessages(err)),
  });

  const handleExcelDownload = async () => {
    setIsExcelLoading(true);
    try {
      const all = (await exportUnconfirmedReservations({ branchId, ...query }, user.token)) || [];
      if (all.length === 0) {
        toast.info('다운로드할 내역이 없습니다');
        return;
      }
      const excelRows = all.map(row => ({
        수업일: row.classDate ?? '',
        시간: `${row.startTime ?? ''}~${row.endTime ?? ''}`,
        수업유형: row.classTypeLabel ?? '',
        수업명: row.className ?? '',
        강사: row.trainerName ?? '',
        회원명: row.memberName ?? '',
        연락처: row.phoneNumber ?? '',
        수강권: row.ticketName ?? '',
        잔여: row.remainingCount == null ? '무제한' : `${row.remainingCount}회`,
        출결상태: STATUS_BADGE[row.status]?.label ?? row.status ?? '',
      }));
      await exportStyledExcel(excelRows, {
        filename: `수업관리_${query.startDate}_${query.endDate}`,
        sheetName: '수업 관리',
      });
    } catch (err) {
      toast.error(errorMessages(err));
    } finally {
      setIsExcelLoading(false);
    }
  };

  const requestProcess = (action, ids) => setPending({ action, ids, includeSettled: false });
  // 선택 중 이미 출결 처리된 줄과, 확인 창 체크 여부에 따른 실제 처리 대상
  const pendingSettled = (pending?.ids || []).map(id => rowCache.current.get(id)).filter(isSettled);
  const pendingTargetIds = !pending ? [] : pending.includeSettled
    ? pending.ids
    : pending.ids.filter(id => !isSettled(rowCache.current.get(id)));
  // 아직 끝나지 않은 수업(예약중 — 수업 전·진행 중)이 처리 대상에 섞였는지. 막지 않고 안내만 한다(10/16 사용자 결정)
  const pendingReservedCount = pendingTargetIds.filter(id => rowCache.current.get(id)?.status === 'RESERVED').length;

  const bulkDisabled = selectedIds.size === 0 || processMutation.isPending;

  if (!branchId) {
    return (
      <AnimationPage>
        <Typography sx={{ py: 4 }}>지점을 선택해주세요.</Typography>
      </AnimationPage>
    );
  }

  return (
    <AnimationPage>
      <Tabs
        value={tab}
        onChange={(_e, value) => setTab(value)}
        sx={{
          mt: 1, minHeight: 40, borderBottom: '1px solid #E8E8E8',
          '& .MuiTab-root': { minHeight: 40, fontSize: '15px', fontWeight: 600 },
          '& .Mui-selected': { color: '#0F7F85 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#0F7F85' },
        }}>
        <Tab value="list" label="수업 목록" />
        <Tab value="consult" label="상담 목록" />
        <Tab value="stats" label="통계" />
        {SHOW_HISTORY_TAB && <Tab value="history" label="이력" />}
        <Tab value="settings" label="설정" />
      </Tabs>
      {tab === 'settings' && <ClassManageSettings branchId={branchId} />}
      {SHOW_HISTORY_TAB && tab === 'history' && <ReservationHistory branchId={branchId} />}
      {tab === 'consult' && <ConsultList branchId={branchId} />}
      {tab === 'stats' && (
        <Box sx={{ pt: 2 }}>
          {/* 통계: 수업 / 상담 전환 */}
          <Tabs value={statsTab} onChange={(_e, value) => setStatsTab(value)} sx={pillTabsSx} aria-label="stats type tabs">
            <Tab value="class" label="수업" />
            <Tab value="consult" label="상담" />
          </Tabs>
          <Box sx={{ pt: 2 }}>
            {statsTab === 'class' ? <ClassAttendanceStats branchId={branchId} /> : <ConsultStats branchId={branchId} />}
          </Box>
        </Box>
      )}
      {tab === 'list' && (
      <Box sx={{ py: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* 필터 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
          <TextField
            type="date" size="small" label="수업일 시작" InputLabelProps={{ shrink: true }} sx={dateFieldSx}
            value={form.startDate} onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))}
          />
          <TextField
            type="date" size="small" label="수업일 종료" InputLabelProps={{ shrink: true }} sx={dateFieldSx}
            value={form.endDate} onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value }))}
          />
          <TextField
            select size="small" label="수업유형" sx={{ minWidth: 130 }}
            value={form.classType} onChange={e => setForm(prev => ({ ...prev, classType: e.target.value }))}>
            {CLASS_TYPE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="강사" sx={{ minWidth: 150 }}
            value={form.trainerId} onChange={e => setForm(prev => ({ ...prev, trainerId: e.target.value }))}>
            <MenuItem value="">전체</MenuItem>
            {trainers.map(trainer => (
              <MenuItem key={trainer.trainerId} value={trainer.trainerId}>{trainer.trainerName}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="출결상태" sx={{ minWidth: 110 }}
            value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}>
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          {/* 검색: 종류(회원·수업명)를 고르고 Enter → 조건이 칩으로 붙고 바로 조회. 회원·수업명은 함께 걸 수 있다(AND) */}
          <TextField
            select size="small" label="검색 대상" sx={{ minWidth: 110 }}
            value={searchField} onChange={e => setSearchField(e.target.value)}>
            {SEARCH_FIELDS.map(field => (
              <MenuItem key={field.value} value={field.value}>{field.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="검색어" sx={{ minWidth: 180 }}
            placeholder={SEARCH_FIELDS.find(f => f.value === searchField)?.placeholder}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) addSearchChip();
            }}
          />
          <Button variant="outlined" onClick={() => (searchText.trim() ? addSearchChip() : handleSearch())} sx={searchButtonSx}>
            조회
          </Button>
          {SEARCH_FIELDS.filter(field => query[field.value]).map(field => (
            <Chip
              key={field.value}
              size="small"
              label={`${field.label}: ${query[field.value]}`}
              onDelete={() => removeSearchChip(field.value)}
              sx={{ height: 30, fontSize: '13px', background: '#F2F5F6', border: '1px solid #E4E9EA' }}
            />
          ))}
        </Box>

        {/* 엑셀 줄: 왼쪽 상태별 건수, 오른쪽 엑셀 다운로드 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <Box>
            {/* 상태별 건수(지점 스케줄과 같은 칩). 누르면 그 상태만, 다시 누르면 전체 */}
            <HeaderSummaryChips>
              {/* 지점 스케줄의 '스케줄' 칩처럼 전체 건수 + 누르면 상태 필터 해제 */}
              <SummaryChipItem type="button" countColor="#5A6468" isActive={false} title="전체 보기" onClick={() => applyStatus('ALL')}>
                전체
                <b>{Object.values(statusCounts).reduce((sum, n) => sum + n, 0).toLocaleString()}건</b>
              </SummaryChipItem>
              {SUMMARY_CHIPS.map(chip => {
                const active = query.status === chip.key;
                return (
                  <React.Fragment key={chip.key}>
                    <SummaryChipDivider>|</SummaryChipDivider>
                    <SummaryChipItem
                      type="button"
                      countColor={chip.color}
                      isActive={active}
                      aria-pressed={active}
                      title={chip.filterable ? (active ? `${chip.label} 필터 해제` : `${chip.label}만 보기`) : chip.label}
                      onClick={() => chip.filterable && applyStatus(active ? 'ALL' : chip.key)}
                      style={chip.filterable ? undefined : { cursor: 'default' }}>
                      {chip.label}
                      <b>{(statusCounts[chip.key] ?? 0).toLocaleString()}건</b>
                    </SummaryChipItem>
                  </React.Fragment>
                );
              })}
            </HeaderSummaryChips>
          </Box>
          <CommonExcelButton onClick={handleExcelDownload} disabled={isExcelLoading || !totalElements}>
            {isExcelLoading ? '다운로드 중...' : '엑셀 다운로드'}
          </CommonExcelButton>
        </Box>

        {/* 목록 */}
        {/* 필터·컬럼명은 고정, 데이터만 표 안에서 스크롤 */}
        <TableContainer
          ref={scrollRef}
          sx={{
            border: '1px solid #E8E8E8', borderRadius: '8px', overflowY: 'auto',
            maxHeight: selectedIds.size > 0 ? 'calc(100vh - 360px)' : 'calc(100vh - 280px)',
          }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ ...headCellSx, ...checkboxCellSx }}>
                  <CustomCheckbox
                    name="unconfirmedAllSelect"
                    checked={allChecked}
                    disabled={selectableRows.length === 0}
                    onChange={e => setAllChecked(e.target.checked)}
                  />
                </TableCell>
                {['수업일', '시간', '수업유형', '수업명', '강사', '회원명', '연락처', '수강권', '잔여', '출결상태', '처리'].map(head => (
                  <TableCell key={head} align="center" sx={headCellSx}>{head}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={12} align="center">불러오는 중...</TableCell></TableRow>
              )}
              {isError && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ color: '#d32f2f' }}>
                    {errorMessages(error)}
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 6, color: '#888' }}>
                    조회된 수업이 없습니다.
                  </TableCell>
                </TableRow>
              )}
              {rows.map(row => (
                <TableRow key={row.reservationId} hover selected={selectedIds.has(row.reservationId)}>
                  <TableCell align="center" sx={checkboxCellSx}>
                    {isSelectable(row) && (
                      <CustomCheckbox
                        name={`unconfirmed-${row.reservationId}`}
                        checked={selectedIds.has(row.reservationId)}
                        onChange={e => setRowChecked(row.reservationId, e.target.checked)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <ScheduleDateLink date={row.classDate} classId={row.courseClassId} classType={row.classType} />
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.startTime}~{row.endTime}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.classTypeLabel}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.className}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.trainerName}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.memberName}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.phoneNumber}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.ticketName}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>{row.remainingCount == null ? '무제한' : `${row.remainingCount}회`}</TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    {STATUS_BADGE[row.status] && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block', px: '8px', py: '2px', borderRadius: '10px', fontSize: '12px',
                          fontWeight: 600, color: STATUS_BADGE[row.status].color,
                          background: STATUS_BADGE[row.status].background,
                        }}>
                        {STATUS_BADGE[row.status].label}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      {isChangeable(row) && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={changeMutation.isPending}
                          onClick={() => setChangeRow(row)}
                          sx={{ minWidth: 0, px: 1.5, fontSize: '12px', borderColor: '#999', color: '#555' }}>
                          변경
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box ref={sentinelRef} sx={{ height: '1px' }} />
          {isFetchingNextPage && (
            <Typography sx={{ textAlign: 'center', py: 1.5, fontSize: '13px', color: '#888' }}>불러오는 중...</Typography>
          )}
        </TableContainer>

        {/* 선택 시 하단 플로팅 액션 바 (일반 회원 관리와 같은 위치·모양) */}
        {selectedIds.size > 0 && (
          <FloatingActionBar sx={{ gap: '12px', padding: '10px 12px 10px 20px' }}>
            <FloatingBarInfo>
              <Box component="span" sx={{ color: '#0F7F85' }}>{selectedIds.size}</Box>건 선택됨
            </FloatingBarInfo>
            <Box sx={{ width: '1px', height: '20px', background: '#E5E5E5' }} />
            {['ATTEND', 'ABSENT', 'CANCEL'].map(action => (
              <Button
                key={action}
                variant="outlined"
                disabled={bulkDisabled}
                onClick={() => requestProcess(action, [...selectedIds])}
                sx={barButtonSx(action)}>
                {ACTIONS[action].label}
              </Button>
            ))}
            <FloatingCloseButton onClick={() => setSelectedIds(new Set())} aria-label="선택 해제">
              ×
            </FloatingCloseButton>
          </FloatingActionBar>
        )}

      </Box>
      )}

      <ChangeAttendanceModal
        row={changeRow}
        onClose={() => setChangeRow(null)}
        onConfirm={action => changeMutation.mutate(action)}
        isPending={changeMutation.isPending}
      />

      <ConfirmModal
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={() => {
          if (pendingTargetIds.length === 0) {
            toast.info('처리할 예약이 없습니다. 이미 처리된 건도 함께 처리하려면 체크해 주세요.');
            return;
          }
          processMutation.mutate({ action: pending.action, ids: pendingTargetIds });
        }}
        message={
          !pending ? ''
            : pendingTargetIds.length === 0
              ? '선택한 예약은 모두 이미 출결 처리됐습니다. 함께 처리하려면 아래를 체크해 주세요.'
              : `선택한 ${pendingTargetIds.length}건을 ${ACTIONS[pending.action].confirmText} 처리하시겠습니까?`
        }
        highlightText={pending ? ACTIONS[pending.action].confirmText : ''}
        highlightColor={pending ? ACTIONS[pending.action].color : undefined}
        confirmText="확인"
        confirmColor={pending ? ACTIONS[pending.action].color : undefined}
        detail={
          pending ? (
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* 선택에 이미 출결 처리된 건이 섞여 있으면 함께 처리할지 묻는다(기본: 미확인만) */}
              {pendingSettled.length > 0 && (
                <Box sx={{ background: '#FFF8E1', border: '1px solid #FFE0A3', borderRadius: '8px', p: '10px 12px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#7A5200' }}>
                    선택한 {pending.ids.length}건 중 <b>{pendingSettled.length}건</b>은 이미 출결 처리됐습니다
                    (출석 {pendingSettled.filter(r => r.status === 'ATTENDED').length}건 · 결석{' '}
                    {pendingSettled.filter(r => r.status === 'ABSENT').length}건).
                  </Typography>
                  <FormControlLabel
                    sx={{ mt: '4px', '& .MuiFormControlLabel-label': { fontSize: '13px', fontWeight: 600 } }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={!!pending.includeSettled}
                        onChange={e => setPending(prev => ({ ...prev, includeSettled: e.target.checked }))}
                        sx={{ '&.Mui-checked': { color: '#262626' } }}
                      />
                    )}
                    label={`이미 처리된 ${pendingSettled.length}건도 함께 ${ACTIONS[pending.action].confirmText} 처리`}
                  />
                </Box>
              )}
              {pendingReservedCount > 0 && (
                <Box sx={{ background: '#E3F2FD', border: '1px solid #BBDEFB', borderRadius: '8px', p: '10px 12px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#0D47A1' }}>
                    <b>{pendingReservedCount}건</b>은 아직 끝나지 않은 수업(예약중)입니다.
                    {pending.action === 'ATTEND' && ' 출석 처리하면 지금 바로 횟수가 차감됩니다.'}
                    {pending.action === 'ABSENT' && ' 결석 처리해도 빈 자리에 대기자가 자동으로 올라가지 않습니다.'}
                  </Typography>
                </Box>
              )}
              {pending.action === 'CANCEL' && (
                <Typography sx={{ fontSize: '14px', color: '#d32f2f' }}>{CANCEL_NOTICE}</Typography>
              )}
            </Box>
          ) : null
        }
      />

      {/* 처리 결과 */}
      <Dialog open={!!result} onClose={() => setResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {result ? `${result.successCount}건 처리되었습니다.` : ''}
        </DialogTitle>
        <DialogContent>
          {result?.failures.length > 0 && (
            <>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#d32f2f', mb: 1 }}>
                처리하지 못한 {result.failures.length}건
              </Typography>
              {result.failures.map(failure => {
                const row = rowCache.current.get(failure.reservationId);
                return (
                  <Typography key={failure.reservationId} sx={{ fontSize: '13px', color: '#333', py: '2px' }}>
                    {row ? `${row.memberName} (${row.classDate})` : `예약 ${failure.reservationId}`} - {failure.message}
                  </Typography>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResult(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </AnimationPage>
  );
}

export default UnconfirmedAttendance;
